import React, { useEffect, useState } from "react";
import { apiFetch } from "../api";
import GenderBadge from "./GenderBadge";
import { CancelIcon, SendIcon } from "./icons";
import { formatDate } from "../utils/dateUtils";

const isPublicSeeker = () =>
  typeof window !== "undefined" &&
  !!localStorage.getItem("publicUserToken");

// Opens when a profile card is clicked. Mirrors the bureau Dashboard's
// expanded profile-card layout (sectioned bullets) so users see the same
// detail set everywhere. The candidate's own phone stays hidden — viewers
// reach out via the listing bureau, which approves and forwards the contact.
const ProfileDetailsModal = ({ isOpen, userId, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [bureauInfo, setBureauInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [interestState, setInterestState] = useState({
    sending: false,
    sent: false,
    error: "",
    message: "",
  });
  const seeker = isPublicSeeker();

  useEffect(() => {
    if (!isOpen || !userId) {
      setProfile(null);
      setBureauInfo(null);
      setError("");
      setInterestState({ sending: false, sent: false, error: "", message: "" });
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    apiFetch(`/public/profile/${userId}`)
      .then((res) => {
        if (cancelled) return;
        setProfile(res?.data || null);
        setBureauInfo(res?.bureauInfo || null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || "Failed to load profile");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, userId]);

  if (!isOpen) return null;

  const heightStr =
    profile && (profile.heightFeet || profile.heightInches)
      ? `${profile.heightFeet ?? 0}ft ${profile.heightInches ?? 0}in`
      : "—";

  const houseLine = (p) => {
    const parts = [];
    if (p.houseSizeMarla) parts.push(`${p.houseSizeMarla} marla`);
    else parts.push("Not specified");
    if (p.houseLocation) parts[parts.length - 1] += `, ${p.houseLocation}`;
    if (p.area) parts[parts.length - 1] += `, ${p.area}`;
    if (p.city) parts[parts.length - 1] += `, ${p.city}`;
    if (p.country) parts[parts.length - 1] += `, ${p.country}`;
    if (p.houseStatus) parts[parts.length - 1] += ` (${p.houseStatus})`;
    return parts[0];
  };

  // Section wrapper matching Dashboard's "label + UL with one LI" pattern.
  const Section = ({ title, children }) => (
    <div className="mt-3 pt-3 border-t border-[var(--color-border)] text-sm text-[var(--color-text)]">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
        {title}
      </div>
      <ul className="list-disc ml-5 mt-1 text-xs space-y-0.5">{children}</ul>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="relative w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden rounded-xl bg-[var(--color-surface)] shadow-lg">
        {/* HEADER */}
        <div className="flex items-start justify-between px-4 sm:px-6 py-4 border-b border-[var(--color-border)] flex-shrink-0">
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-0.5">
              ID {profile?.referenceId || "—"}
            </div>
            <h3 className="text-lg font-semibold text-[var(--color-text-strong)] truncate flex items-center gap-2">
              <span>{profile?.fullName || "Profile"}</span>
              {profile?.gender && <GenderBadge gender={profile.gender} />}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--color-text)] hover:text-[var(--color-text-strong)]"
          >
            <CancelIcon className="w-4 h-4" />
            Close
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {loading && (
            <div className="text-sm text-[var(--color-text-muted)]">
              Loading profile…
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-[var(--color-danger-border)] bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger-text)]">
              {error}
            </div>
          )}

          {!loading && !error && profile && (
            <>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                Personal Details
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm text-[var(--color-text)] mb-3">
                <span>•</span>
                <span>{profile.age ?? "—"}</span>
                <span>•</span>
                <span className="capitalize">{profile.gender || "—"}</span>
                <span>•</span>
                <span>{heightStr}</span>
                <span>•</span>
                <span className="capitalize">
                  {profile.maritalStatus || "—"}
                </span>
              </div>

              <Section title="Date of Birth">
                <li>
                  <span className="text-xs">
                    {profile.dob ? formatDate(profile.dob) : "Not specified"}
                  </span>
                </li>
              </Section>

              <Section title="Caste">
                <li>
                  <span className="text-xs">
                    {profile.cast || profile.caste || "Not specified"}
                  </span>
                </li>
              </Section>

              <Section title="House">
                <li>
                  <span>{houseLine(profile)}</span>
                </li>
              </Section>

              <Section title="Profession">
                <li>
                  <span>{profile.profession || "Not specified"}</span>
                </li>
              </Section>

              <Section title="Monthly Income">
                <li>
                  <span>
                    {profile.monthlyIncome
                      ? `Rs ${Number(profile.monthlyIncome).toLocaleString()}`
                      : "Not specified"}
                  </span>
                </li>
              </Section>

              <Section title="Qualification">
                <li>Education: {profile.education || "Not specified"}</li>
                <li>
                  Institute of Education:{" "}
                  {profile.instituteOfQualification || "Not specified"}
                </li>
              </Section>

              <div className="mt-3 pt-3 border-t border-[var(--color-border)]">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                  Family Information
                </div>
                <div className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)]">
                  Parents Details
                </div>
                <ul className="list-disc ml-5 mt-1 text-xs space-y-0.5">
                  <li>
                    Father: {profile.fatherOccupation || "Not specified"}
                    {profile.fatherDeceased ? " (Deceased)" : ""}
                  </li>
                  <li>
                    Mother: {profile.motherOccupation || "Not specified"}
                    {profile.motherDeceased ? " (Deceased)" : ""}
                  </li>
                </ul>
              </div>

              {Array.isArray(profile.siblings) &&
                profile.siblings.length > 0 && (
                  <Section title="Siblings">
                    {profile.siblings.map((s, i) => (
                      <li key={i}>
                        <span className="capitalize">
                          {s.relation || "—"}
                        </span>{" "}
                        — {s.qualification || "—"} / {s.profession || "—"} /{" "}
                        <span className="capitalize">
                          {s.maritalStatus || "—"}
                        </span>
                      </li>
                    ))}
                  </Section>
                )}

              {profile.preferences && (
                <Section title="Preferences">
                  <li>
                    <span className="whitespace-pre-wrap">
                      {profile.preferences}
                    </span>
                  </li>
                </Section>
              )}

              {/* Express interest — only for logged-in profile seekers. */}
              {seeker && (
                <div className="mt-4 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 text-sm">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
                    Interested?
                  </div>
                  {interestState.sent ? (
                    <div className="text-sm text-[var(--color-text)]">
                      Request sent. The bureau will review and share the
                      contact once approved. You'll find this profile under{" "}
                      <span className="font-semibold">My Interests</span>.
                    </div>
                  ) : (
                    <>
                      <p className="text-xs text-[var(--color-text-muted)] mb-2">
                        Send an interest request to the bureau. They'll approve
                        it and share the candidate's contact privately.
                      </p>
                      <textarea
                        rows={2}
                        value={interestState.message}
                        onChange={(e) =>
                          setInterestState((s) => ({
                            ...s,
                            message: e.target.value,
                          }))
                        }
                        placeholder="Optional note for the bureau"
                        className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm mb-2"
                      />
                      {interestState.error && (
                        <div className="text-xs text-[var(--color-danger-text)] mb-2">
                          {interestState.error}
                        </div>
                      )}
                      <button
                        type="button"
                        disabled={interestState.sending}
                        onClick={async () => {
                          setInterestState((s) => ({
                            ...s,
                            sending: true,
                            error: "",
                          }));
                          try {
                            await apiFetch("/public/interests", {
                              method: "POST",
                              body: JSON.stringify({
                                profileId: userId,
                                message: interestState.message,
                              }),
                            });
                            setInterestState({
                              sending: false,
                              sent: true,
                              error: "",
                              message: "",
                            });
                          } catch (err) {
                            setInterestState((s) => ({
                              ...s,
                              sending: false,
                              error:
                                err.message ||
                                "Failed to send interest request",
                            }));
                          }
                        }}
                        className="inline-flex items-center gap-2 rounded-lg bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] text-[var(--color-on-primary)] disabled:opacity-60 px-3 py-1.5 text-sm font-semibold transition-colors"
                      >
                        <SendIcon className="w-4 h-4" />
                        {interestState.sending
                          ? "Sending…"
                          : "Express Interest"}
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Bureau Information — the only contact path. */}
              {bureauInfo && (
                <div className="mt-4 rounded-2xl border-2 border-[var(--color-primary)] bg-[var(--color-primary-soft)] p-3 text-sm text-[var(--color-text)] shadow-md">
                  <div className="font-semibold text-[var(--color-text-strong)] mb-2 flex items-center gap-2">
                    Bureau Information
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-[var(--color-primary)] text-[var(--color-on-primary)]">
                      Contact Info
                    </span>
                  </div>
                  <div className="grid gap-1">
                    <div>
                      <span className="font-medium">Name:</span>{" "}
                      {bureauInfo.name || "—"}
                    </div>
                    <div>
                      <span className="font-medium">Owner:</span>{" "}
                      {bureauInfo.ownerName || "—"}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">Contact:</span>
                      {bureauInfo.contactNumber ? (
                        <a
                          href={`tel:${bureauInfo.contactNumber}`}
                          className="inline-flex items-center px-3 py-1 rounded-lg bg-white border border-[var(--color-primary)] text-[var(--color-primary)] font-mono font-semibold shadow-sm hover:bg-[var(--color-primary)] hover:text-[var(--color-on-primary)] transition-colors"
                        >
                          {bureauInfo.contactNumber}
                        </a>
                      ) : (
                        <span>—</span>
                      )}
                    </div>
                    {bureauInfo.address && (
                      <div>
                        <span className="font-medium">Address:</span>{" "}
                        {bureauInfo.address}
                      </div>
                    )}
                  </div>
                  <p className="text-[11px] text-[var(--color-text-muted)] pt-2 border-t border-[var(--color-primary)] mt-2">
                    Reach out to the bureau to express interest. They will
                    review the request and share the candidate's contact after
                    approval.
                  </p>
                </div>
              )}

              <div className="mt-3 pt-3 border-t border-[var(--color-border)] text-[10px] text-[var(--color-text-muted)]">
                {profile.createdAt && (
                  <div>
                    Listed {formatDate(profile.createdAt)}
                    {profile.updatedAt && (
                      <> &nbsp;·&nbsp; Last edited {formatDate(profile.updatedAt)}</>
                    )}
                  </div>
                )}
                <div className="mt-0.5">
                  Views {profile.views ?? 0} &nbsp;·&nbsp; Status{" "}
                  {profile.status || "active"}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileDetailsModal;
